if (!customElements.get('localization-form-v2')) {
  class LocalizationFormV2 extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.localization-v2__trigger');
      this.panel = this.querySelector('.localization-v2__panel');
      this.form = this.querySelector('.localization-v2__form');
      this.overlay = this.querySelector('.localization-v2__overlay');
      this.closeBtn = this.querySelector('.localization-v2__close-btn');
      this.countryItems = this.querySelectorAll('.localization-v2__country-item');
      this.countryInput = this.querySelector('input[name="country_code"]');
      this.languageSelect = this.querySelector('.localization-v2__language-select');
      this.localeInput = this.querySelector('input[name="locale_code"]');
      this.isOpen = false;
      this.isMobile = this.classList.contains('localization-v2--mobile');
      this.mobileHideSelectors = this.parseMobileHideSelectors();

      this.handleClickOutside = this.handleClickOutside.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleCountryClick = this.handleCountryClick.bind(this);
      this.handleCountryKeydown = this.handleCountryKeydown.bind(this);
      this.handleLanguageChange = this.handleLanguageChange.bind(this);

      this.trigger?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.togglePanel();
      });

      this.countryItems.forEach((item) => {
        item.addEventListener('click', this.handleCountryClick);
        item.addEventListener('keydown', this.handleCountryKeydown);
      });

      this.languageSelect?.addEventListener('change', this.handleLanguageChange);

      this.closeBtn?.addEventListener('click', () => this.hidePanel());
      this.overlay?.addEventListener('click', () => this.hidePanel());
    }

    parseMobileHideSelectors() {
      const selectorsAttr = this.dataset.mobileHideSelectors;
      if (!selectorsAttr) return [];
      return selectorsAttr
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }

    hideExternalElements() {
      if (!this.isMobile || this.mobileHideSelectors.length === 0) return;
      this.mobileHideSelectors.forEach((selector) => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach((el) => {
            el.dataset.localizationHidden = el.style.display || '';
            el.style.display = 'none';
          });
        } catch (e) {
          console.warn('Invalid selector for localization hide: ' + selector);
        }
      });
    }

    showExternalElements() {
      if (!this.isMobile || this.mobileHideSelectors.length === 0) return;
      this.mobileHideSelectors.forEach((selector) => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach((el) => {
            if (el.dataset.localizationHidden !== undefined) {
              el.style.display = el.dataset.localizationHidden || '';
              delete el.dataset.localizationHidden;
            }
          });
        } catch (e) {
          console.warn('Invalid selector for localization show: ' + selector);
        }
      });
    }

    lockScroll() {
      if (!this.isMobile) return;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.dataset.scrollY = window.scrollY;
    }

    unlockScroll() {
      if (!this.isMobile) return;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      const scrollY = document.body.dataset.scrollY;
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10));
        delete document.body.dataset.scrollY;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('keydown', this.handleKeydown);

      this.countryItems?.forEach((item) => {
        item.removeEventListener('click', this.handleCountryClick);
        item.removeEventListener('keydown', this.handleCountryKeydown);
      });

      this.languageSelect?.removeEventListener('change', this.handleLanguageChange);
    }

    togglePanel() {
      this.isOpen ? this.hidePanel() : this.showPanel();
    }

    showPanel() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.panel.removeAttribute('hidden');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.overlay?.classList.add('is-visible');
      this.hideExternalElements();
      this.lockScroll();

      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('keydown', this.handleKeydown);

      requestAnimationFrame(() => {
        const selectedItem = this.querySelector('.localization-v2__country-item[aria-current="true"]');
        const firstItem = this.querySelector('.localization-v2__country-item');
        (selectedItem || firstItem)?.focus();
        selectedItem?.scrollIntoView({ block: 'nearest' });
      });
    }

    hidePanel() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.panel.setAttribute('hidden', '');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.overlay?.classList.remove('is-visible');
      this.showExternalElements();
      this.unlockScroll();

      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    handleClickOutside(event) {
      if (!this.contains(event.target)) {
        this.hidePanel();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.hidePanel();
        this.trigger?.focus();
      }
    }

    handleCountryClick(event) {
      const countryCode = event.currentTarget.dataset.value;
      this.selectCountry(countryCode);
    }

    handleCountryKeydown(event) {
      const items = Array.from(this.countryItems);
      const currentIndex = items.indexOf(event.currentTarget);

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.selectCountry(event.currentTarget.dataset.value);
          break;
        case 'ArrowDown':
          event.preventDefault();
          items[currentIndex < items.length - 1 ? currentIndex + 1 : 0]?.focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          items[currentIndex > 0 ? currentIndex - 1 : items.length - 1]?.focus();
          break;
      }
    }

    selectCountry(countryCode) {
      if (!this.countryInput || !countryCode) return;
      this.countryInput.value = countryCode;
      this.form?.submit();
    }

    handleLanguageChange(event) {
      const languageCode = event.target.value;
      if (!this.localeInput || !languageCode) return;
      this.localeInput.value = languageCode;
      this.form?.submit();
    }
  }

  customElements.define('localization-form-v2', LocalizationFormV2);
}
